import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { chapterImageProxyUrl, getChapterPages, getChapters } from '../services/api';

/**
 * Once any image in this tab has had to fall back to the proxy, the
 * MangaDex@Home CDN is clearly rejecting our direct loads (it blocks some
 * client fingerprints with a 404). Remembering that for the session lets the
 * remaining pages skip the doomed direct attempt and go straight to the proxy,
 * so we don't eat a failed round-trip per page. Cleared naturally on a new tab.
 */
const proxyPreferred = () => {
  try { return sessionStorage.getItem('mdImgProxy') === '1'; } catch { return false; }
};
const markProxyPreferred = () => {
  try { sessionStorage.setItem('mdImgProxy', '1'); } catch { /* private mode */ }
};

/**
 * One reader page image. Loads from the MangaDex@Home CDN first (fast, zero
 * bandwidth on us); if that node 404s it self-heals by falling back to our
 * server-side proxy, which re-routes via a fresh at-home node. Only if BOTH
 * fail do we show a lightweight placeholder. `referrerPolicy="no-referrer"`
 * because MangaDex also rejects a foreign Referer on some nodes.
 */
const ReaderImage = ({ directSrc, proxySrc, index }) => {
  // 'direct' -> CDN, 'proxy' -> our server, 'failed' -> both gave up
  const [stage, setStage] = useState(() => (proxyPreferred() ? 'proxy' : 'direct'));
  // Reset when the underlying chapter/page changes.
  useEffect(() => { setStage(proxyPreferred() ? 'proxy' : 'direct'); }, [directSrc, proxySrc]);

  if (stage === 'failed') {
    return (
      <div className="w-full py-10 text-center text-zinc-600 text-xs border-b border-zinc-900">
        Page {index + 1} couldn’t load
      </div>
    );
  }
  return (
    <img
      src={stage === 'direct' ? directSrc : proxySrc}
      alt={`Page ${index + 1}`}
      loading={index < 3 ? 'eager' : 'lazy'}
      className="w-full block"
      referrerPolicy="no-referrer"
      onError={() => setStage((s) => {
        if (s === 'direct') { markProxyPreferred(); return 'proxy'; }
        return 'failed';
      })}
    />
  );
};

/**
 * Manhwa reader — vertical long-strip layout (the dominant format for
 * webtoons / Korean manhwa). Pages are loaded directly from MangaDex's CDN
 * via base_url + filename, so our backend never proxies image bytes.
 *
 * Token in base_url expires (~15 min); for sessions longer than that the
 * user just clicks next chapter and we get a fresh URL. We don't try to
 * pre-cache.
 */
const ManhuaReaderPage = () => {
  const { id: mangaId, chapterId } = useParams();
  const navigate = useNavigate();

  const [pages, setPages] = useState(null);     // { base_url, hash, pages, quality }
  const [chapterList, setChapterList] = useState(null);
  const [error, setError] = useState(null);
  const [dataSaver, setDataSaver] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);  // bump to re-fetch a fresh token

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    setError(null);
    getChapterPages(chapterId, { dataSaver })
      .then((p) => { if (!cancelled) setPages(p); })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Could not load pages');
        }
      });
    return () => { cancelled = true; };
  }, [chapterId, dataSaver, reloadKey]);

  // Load chapter list once for prev/next navigation
  useEffect(() => {
    let cancelled = false;
    getChapters(mangaId, { order: 'asc', limit: 500 })
      .then((data) => { if (!cancelled) setChapterList(data.chapters); })
      .catch(() => { /* navigation is a nice-to-have; silent fail OK */ });
    return () => { cancelled = true; };
  }, [mangaId]);

  // Reset scroll position when switching chapters
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [chapterId]);

  const { prevChapter, nextChapter, currentIndex } = useMemo(() => {
    if (!chapterList) return { prevChapter: null, nextChapter: null, currentIndex: -1 };
    const idx = chapterList.findIndex((c) => c.chapter_id === chapterId);
    return {
      currentIndex: idx,
      prevChapter: idx > 0 ? chapterList[idx - 1] : null,
      nextChapter: idx >= 0 && idx < chapterList.length - 1 ? chapterList[idx + 1] : null,
    };
  }, [chapterList, chapterId]);

  const currentChapter = chapterList?.[currentIndex];
  const qualityFolder = pages?.quality === 'data-saver' ? 'data-saver' : 'data';

  const goPrev = () => prevChapter && navigate(`/manhua/${mangaId}/read/${prevChapter.chapter_id}`);
  const goNext = () => nextChapter && navigate(`/manhua/${mangaId}/read/${nextChapter.chapter_id}`);

  // Keyboard shortcuts: ← / → switch chapter
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') navigate(`/manhua/${mangaId}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevChapter, nextChapter, mangaId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-black text-zinc-100">
      {/* Top bar */}
      <header className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-40 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <Link to={`/manhua/${mangaId}`} className="text-sm text-zinc-300 hover:text-rose-400 truncate">
            ← Back to chapters
          </Link>
          <div className="text-xs text-zinc-500 truncate hidden sm:block">
            {currentChapter
              ? `Ch ${currentChapter.chapter_number || '?'}${currentChapter.title ? ` — ${currentChapter.title}` : ''}`
              : 'Loading...'}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDataSaver((v) => !v)}
              className={`text-xs px-2 py-1 rounded border ${dataSaver ? 'bg-rose-500 text-white border-rose-500' : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'}`}
              title="Toggle data saver (lower quality, faster)"
            >
              {dataSaver ? '⚡ Saver' : 'HD'}
            </button>
          </div>
        </div>
      </header>

      {/* Pages — vertical strip */}
      <main className="max-w-3xl mx-auto">
        {error && (
          <div className="px-4 py-12 text-center text-amber-300">
            <p>{error}</p>
            <p className="text-zinc-500 text-xs mt-1">MangaDex’s servers can be flaky — a retry often fixes it.</p>
            <div className="flex items-center justify-center gap-4 mt-3">
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="text-sm px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-500 text-white"
              >
                ↻ Retry
              </button>
              <Link to={`/manhua/${mangaId}`} className="text-rose-400 hover:underline text-sm">
                ← Back to chapter list
              </Link>
            </div>
          </div>
        )}

        {!error && !pages && (
          <div className="px-4 py-16 text-center text-zinc-500 text-sm">Fetching pages from MangaDex CDN…</div>
        )}

        {!error && pages && pages.pages.length === 0 && (
          <div className="px-4 py-16 text-center text-zinc-400">
            <p className="mb-2">This chapter has no pages on MangaDex.</p>
            {currentChapter?.external_url && (
              <a href={currentChapter.external_url} target="_blank" rel="noopener noreferrer"
                 className="text-rose-400 hover:underline">
                Read on the scanlator's site ↗
              </a>
            )}
          </div>
        )}

        {!error && pages && pages.pages.length > 0 && (
          <div className="flex flex-col">
            {pages.pages.map((filename, i) => (
              <ReaderImage
                key={`${chapterId}-${reloadKey}-${i}`}
                index={i}
                directSrc={`${pages.base_url}/${qualityFolder}/${pages.hash}/${filename}`}
                proxySrc={chapterImageProxyUrl(chapterId, i, { dataSaver })}
              />
            ))}
          </div>
        )}
      </main>

      {/* Bottom nav */}
      <footer className="bg-zinc-950 border-t border-zinc-800 sticky bottom-0 z-40 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={goPrev}
            disabled={!prevChapter}
            className={`px-4 py-2 rounded text-sm font-medium ${prevChapter ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100' : 'bg-zinc-900 text-zinc-600 cursor-not-allowed'}`}
          >
            ← Prev
          </button>
          <div className="text-xs text-zinc-500">
            {currentIndex >= 0 && chapterList ? `${currentIndex + 1} / ${chapterList.length}` : ''}
          </div>
          <button
            onClick={goNext}
            disabled={!nextChapter}
            className={`px-4 py-2 rounded text-sm font-medium ${nextChapter ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-zinc-900 text-zinc-600 cursor-not-allowed'}`}
          >
            Next →
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ManhuaReaderPage;
